import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { adminAPI } from '../../services/api';
import { Plus, Edit, Trash2, Building2, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';

const CompaniesPage = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const queryClient = useQueryClient();

  const { data: companies, isLoading, error } = useQuery('companies', adminAPI.getCompanies);

  const createMutation = useMutation(adminAPI.createCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries('companies');
      setShowCreateModal(false);
      toast.success('Company created successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create company');
    }
  });

  const updateMutation = useMutation(
    ({ id, data }) => adminAPI.updateCompany(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('companies');
        setEditingCompany(null);
        toast.success('Company updated successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Failed to update company');
      }
    }
  );

  const deleteMutation = useMutation(adminAPI.deleteCompany, {
    onSuccess: () => {
      queryClient.invalidateQueries('companies');
      toast.success('Company deleted successfully');
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete company');
    }
  });

  const handleDelete = (company) => {
    if (window.confirm(`Are you sure you want to delete ${company.name}? This action cannot be undone.`)) {
      deleteMutation.mutate(company.id);
    }
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingSpinner className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">Failed to load companies. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Companies</h1>
          <p className="mt-2 text-gray-600">Manage company accounts and access</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn btn-primary flex items-center"
        >
          <Plus className="h-5 w-5 mr-2" />
          Add Company
        </button>
      </div>

      {/* Companies Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {companies?.data?.map((company) => (
          <div key={company.id} className="card p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-semibold text-gray-900">{company.name}</h3>
                  <p className="text-sm text-gray-500">{company.email}</p>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setEditingCompany(company)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(company)}
                  className="text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Projects</span>
                <span className="font-medium">{company.project_count}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">Cameras</span>
                <span className="font-medium">{company.camera_count}</span>
              </div>
              <div className="flex items-center text-sm text-gray-500 mt-3">
                <Calendar className="h-4 w-4 mr-1" />
                Created {format(new Date(company.created_at), 'MMM d, yyyy')}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingCompany) && (
        <CompanyModal
          company={editingCompany}
          onClose={() => {
            setShowCreateModal(false);
            setEditingCompany(null);
          }}
          onSubmit={(data) => {
            if (editingCompany) {
              updateMutation.mutate({ id: editingCompany.id, data });
            } else {
              createMutation.mutate(data);
            }
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      )}
    </div>
  );
};

const CompanyModal = ({ company, onClose, onSubmit, isLoading }) => {
  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: company || {}
  });

  const handleFormSubmit = (data) => {
    onSubmit(data);
    reset();
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {company ? 'Edit Company' : 'Create Company'}
          </h3>
          
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
            <div>
              <label className="form-label">Company Name</label>
              <input
                type="text"
                className="form-input"
                placeholder="Enter company name"
                {...register('name', { required: 'Company name is required' })}
              />
              {errors.name && <p className="form-error">{errors.name.message}</p>}
            </div>

            <div>
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-input"
                placeholder="Enter email address"
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /\S+@\S+\.\S+/,
                    message: 'Please enter a valid email'
                  }
                })}
              />
              {errors.email && <p className="form-error">{errors.email.message}</p>}
            </div>

            {!company && (
              <div>
                <label className="form-label">Password</label>
                <input
                  type="password"
                  className="form-input"
                  placeholder="Enter password"
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                />
                {errors.password && <p className="form-error">{errors.password.message}</p>}
              </div>
            )}

            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="flex-1 btn btn-primary flex items-center justify-center"
              >
                {isLoading ? (
                  <>
                    <LoadingSpinner size="small" className="mr-2" />
                    {company ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  company ? 'Update' : 'Create'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CompaniesPage;
